import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import ManageTimeForm from '../components/timeclock/ManageTimeForm';
import PreviousTimeslips from '../components/timeclock/PreviousTimeslips';

import { useAuth } from '../context/AuthContext';
import { timeslipsAPI, showConfirmModal } from '../services/api';
import { toast } from 'sonner';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';

const Timeclock = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // If the user arrived from a project's Labor Summary tab, the URL carries
  // ?project=<id>. Show a Back button that returns them to that tab.
  const fromProjectId = searchParams.get('project');
  const [date, setDate] = useState('');
  const [calOpen, setCalOpen] = useState(false);
  const [selectedEmployee, setSelectedEmployee] = useState('');
  const [vehicleExpense, setVehicleExpense] = useState('');
  const [fuelExpense, setFuelExpense] = useState('');
  const [timeCards, setTimeCards] = useState([{ project: '', hours: '' }]);
  const [searchTerm, setSearchTerm] = useState('');
  const [slipSort, setSlipSort] = useState({ column: 'date', direction: 'desc' });
  const [projects, setProjects] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [editingSlipId, setEditingSlipId] = useState(null);
  const editingSlipIdRef = useRef(null);
  const [timeslips, setTimeslips] = useState([]);
  // How far back to load timeslips. Default: 2 weeks — covers the
  // typical edit/audit window for a payroll cycle without dragging
  // tens of thousands of legacy rows onto the page. User can widen
  // via the dropdown in PreviousTimeslips. `null` => all-time.
  // Persisted per browser so the user's choice sticks between visits
  // (they were confused when it silently reset to 2 weeks each time).
  const [windowDays, setWindowDays] = useState(() => {
    try {
      const v = localStorage.getItem('restopm:timeclock:windowDays');
      if (v === null) return 14;
      return v === 'all' ? null : Number(v);
    } catch { return 14; }
  });
  useEffect(() => {
    try {
      localStorage.setItem('restopm:timeclock:windowDays', windowDays === null ? 'all' : String(windowDays));
    } catch { /* ignore */ }
  }, [windowDays]);
  const [slipsLoading, setSlipsLoading] = useState(false);

  // Debounce the search box so we don't refetch on every keystroke.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch((searchTerm || '').trim()), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  // Re-fetch timeslips whenever the window (or an active search) changes.
  // The initial render also runs this (since windowDays has a default), so
  // we drop the duplicate fetch from the legacy mount effect below.
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const token = localStorage.getItem('token');
    let url = `${API_URL}/api/timeslips`;
    // When the user is actively searching, look across ALL dates so an entry
    // is always findable no matter how old it is. Otherwise the default
    // 14-day window hides older labor (e.g. a job worked several weeks ago),
    // which made entries look "missing" even though they were saved fine.
    const searching = debouncedSearch.length >= 2;
    if (!searching && windowDays !== null) {
      const since = new Date();
      since.setDate(since.getDate() - windowDays);
      url += `?date_from=${since.toISOString().slice(0, 10)}`;
    }
    setSlipsLoading(true);
    fetch(url, { headers: token ? { 'Authorization': `Bearer ${token}` } : {} })
      .then(readResponseJSON)
      .then(d => setTimeslips(d.timeslips || []))
      .catch(() => {})
      .finally(() => setSlipsLoading(false));
  }, [windowDays, debouncedSearch]);

  useEffect(() => {
    sessionStorage.removeItem('editingSlipId');
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    // Timeslips are loaded by the windowDays effect above. Here we only
    // bring in the projects + employees needed for the form, and apply
    // any ?edit= / ?project= deep-link from the URL once both lists land.
    Promise.all([
      // Only fetch active projects you might log time against — open & AR.
      // Push the status filter into Mongo (comma-separated → $in) so the
      // payload drops from ~5,700 closed/dead docs to just the ~few-hundred
      // active ones. Combined with `slim=true` this keeps the dropdown
      // loading in well under a second even on prod.
      fetch(`${API_URL}/api/projects?slim=true&status=open,ar`).then(readResponseJSON).then(d => ({
        projects: d.projects || [],
      })),
      fetch(`${API_URL}/api/employees?active=true`).then(readResponseJSON),
    ]).then(([projData, empData]) => {
      const projs = projData.projects || [];
      const emps = empData.employees || [];
      setProjects(projs);
      setEmployees(emps);

      const editId = searchParams.get('edit');
      if (editId) {
        // Edit deep-link — slip may not be in the default 2-week window,
        // so fetch the single slip directly instead of relying on the list.
        fetch(`${API_URL}/api/timeslips/${editId}`).then(readResponseJSON).then(slip => {
          if (slip && slip.slip_id) {
            setEditingSlipId(editId);
            editingSlipIdRef.current = editId;
            sessionStorage.setItem('editingSlipId', editId);
            setDate(slip.date || '');
            setSelectedEmployee(slip.employee || '');
            setVehicleExpense(slip.vehicle || '0');
            setFuelExpense(slip.fuel_expense || '0');
            setTimeCards([{ project: slip.project_number || '', hours: String(slip.hours || '') }]);
          }
        }).catch(() => {});
      } else {
        const projectParam = searchParams.get('project');
        if (projectParam) {
          setTimeCards([{ project: projectParam, hours: '' }]);
        }
      }
    }).catch(() => {});
  }, []);

  const projectOptions = projects.map((p) => {
    // Legacy data has both `customer` and `customer_name` populated with the
    // same value, which used to print twice in the dropdown label. Pick the
    // longer/canonical name and never concatenate the two.
    const a = (p.customer_name || '').trim();
    const b = (p.customer || '').trim();
    const name = a && b
      ? (a.toLowerCase() === b.toLowerCase() ? a : (a.length >= b.length ? a : b))
      : (a || b);
    return {
      value: p.project_id,
      label: `[${p.project_id}]${name}${p.job_location ? ' - ' + p.job_location : ''}${p.job_type ? ' [' + p.job_type + ']' : ''}`,
    };
  });

  const empRateMap = {};
  employees.forEach(e => {
    const name = `${e.last_name}, ${e.first_name}`;
    if (e.pay_rate) empRateMap[name] = e.pay_rate;
  });

  const clearForm = () => {
    setDate(''); setSelectedEmployee(''); setVehicleExpense(''); setFuelExpense('');
    setTimeCards([{ project: '', hours: '' }]);
    setEditingSlipId(null);
    editingSlipIdRef.current = null;
    sessionStorage.removeItem('editingSlipId');
  };

  const handleEditSlip = (slip) => {
    const id = slip.slip_id;
    setEditingSlipId(id);
    editingSlipIdRef.current = id;
    sessionStorage.setItem('editingSlipId', id);
    setDate(slip.date || '');
    // Dropdown value is the full "Last, First" name.
    setSelectedEmployee(slip.employee || '');
    setVehicleExpense(slip.vehicle || '0');
    setFuelExpense(slip.fuel_expense || '0');
    setTimeCards([{ project: slip.project_number || '', hours: String(slip.hours || '') }]);
  };

  const handleSaveTime = async (e) => {
    if (!date) { toast.error('Please enter a date'); return; }
    if (!selectedEmployee) { toast.error('Please select an employee'); return; }
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    // selectedEmployee is already the "Last, First" string from the dropdown.
    const empName = selectedEmployee;
    const validCards = timeCards.filter(tc => tc.project && tc.hours);
    if (validCards.length === 0) { toast.error('Please select a project and enter hours'); return; }

    const domId = e?.currentTarget?.getAttribute('data-editing-id');
    const currentEditId = domId || editingSlipIdRef.current || sessionStorage.getItem('editingSlipId') || editingSlipId;

    const token = localStorage.getItem('token');
    const headers = { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` };

    try {
      if (currentEditId) {
        const tc = validCards[0];
        const projObj = projects.find(p => p.project_id === tc.project);
        const entry = {
          employee: empName, date,
          customer: projObj?.customer || '', project: projObj?.job_location || '',
          project_address: projObj?.address || '', project_number: projObj?.project_id || tc.project,
          job_type: projObj?.job_type || '', tech_temps: '',
          vehicle: vehicleExpense || '$0.00', fuel_expense: fuelExpense || '$0.00',
          rate: '', hours: parseFloat(tc.hours) || 0,
        };
        await fetch(`${API_URL}/api/timeslips/${currentEditId}`, {
          method: 'PUT', headers,
          body: JSON.stringify(entry),
        }).then(r => {
          if (!r.ok) throw new Error(`Save failed (HTTP ${r.status})`);
        });
      } else {
        // Vehicle/Fuel expenses entered once at the top of the timecard get
        // split EVENLY across the projects the employee worked that day.
        // Single project => full amount; two projects => half each; etc.
        const parseMoney = (v) => {
          const n = parseFloat(String(v ?? '').replace(/[$,\s]/g, ''));
          return Number.isFinite(n) ? n : 0;
        };
        const splitCount = validCards.length || 1;
        const vehicleEach = (parseMoney(vehicleExpense) / splitCount).toFixed(2);
        const fuelEach = (parseMoney(fuelExpense) / splitCount).toFixed(2);
        for (const tc of validCards) {
          const projObj = projects.find(p => p.project_id === tc.project);
          const entry = {
            employee: empName, date,
            customer: projObj?.customer || '', project: projObj?.job_location || '',
            project_address: projObj?.address || '', project_number: projObj?.project_id || tc.project,
            job_type: projObj?.job_type || '', tech_temps: '',
            vehicle: vehicleEach, fuel_expense: fuelEach,
            rate: '', hours: parseFloat(tc.hours) || 0,
          };
          const res = await fetch(`${API_URL}/api/timeslips`, {
            method: 'POST', headers,
            body: JSON.stringify(entry),
          });
          if (!res.ok) {
            throw new Error(`Save failed for ${tc.project} (HTTP ${res.status})`);
          }
        }
      }
      // Refresh — keep the same window the user has currently selected.
      let refreshUrl = `${API_URL}/api/timeslips`;
      if (windowDays !== null) {
        const since = new Date();
        since.setDate(since.getDate() - windowDays);
        refreshUrl += `?date_from=${since.toISOString().slice(0, 10)}`;
      }
      const res = await fetch(refreshUrl, { headers: { 'Authorization': `Bearer ${token}` } });
      const { data } = await readResponse(res);
      setTimeslips(data.timeslips || []);
      clearForm();
      toast.success('Time saved successfully');
    } catch (err) {
      toast.error(err?.message || 'Failed to save time');
    }
  };

  const handleAddProject = () => {
    setTimeCards(prev => [...prev, { project: '', hours: '' }]);
  };

  const handleDeleteSlip = async (slip) => {
    if (!(await showConfirmModal('Are you sure you want to delete this timeslip?'))) return;
    try {
      await timeslipsAPI.delete(slip.slip_id);
      setTimeslips(prev => prev.filter(s => s.slip_id !== slip.slip_id));
      toast.success('Timeslip deleted');
    } catch (error) {
      if (error.response?.status !== 401) {
        toast.error('Failed to delete timeslip');
      }
    }
  };

  return (
    <Layout>
      <div className="p-8">
        <div className="mb-6 flex items-start gap-4">
          {fromProjectId && (
            <button
              onClick={() => navigate(`/projects/${fromProjectId}?tab=labor`)}
              className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded transition-colors text-sm mt-1"
              data-testid="timeclock-back-btn"
            >
              Back
            </button>
          )}
          <div>
            <h2 className="text-3xl text-[#5B8DB8] font-light mb-1">Timeclock</h2>
            <h1 className="text-4xl text-[#5B8DB8] font-light" suppressHydrationWarning>{editingSlipId ? 'Edit Slip' : 'All Employees'}</h1>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-8">
          <ManageTimeForm
            date={date} setDate={setDate}
            calOpen={calOpen} setCalOpen={setCalOpen}
            selectedEmployee={selectedEmployee} setSelectedEmployee={setSelectedEmployee}
            vehicleExpense={vehicleExpense} setVehicleExpense={setVehicleExpense}
            fuelExpense={fuelExpense} setFuelExpense={setFuelExpense}
            timeCards={timeCards} setTimeCards={setTimeCards}
            editingSlipId={editingSlipId}
            employees={employees.filter(e => (e.roles || []).some(r => r === 'Estimator' || r === 'Labor'))}
            projectOptions={projectOptions}
            onAddProject={handleAddProject}
            onSaveTime={handleSaveTime}
          />
          <PreviousTimeslips
            timeslips={timeslips}
            searchTerm={searchTerm} setSearchTerm={setSearchTerm}
            slipSort={slipSort} setSlipSort={setSlipSort}
            empRateMap={empRateMap}
            onEditSlip={handleEditSlip}
            isAdmin={user?.is_admin}
            onDeleteSlip={handleDeleteSlip}
            windowDays={windowDays}
            setWindowDays={setWindowDays}
            slipsLoading={slipsLoading}
          />
        </div>
      </div>
    </Layout>
  );
};

export default Timeclock;
